import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

export type ThemeVariant = 'cyber' | 'terminal';

interface ThemeContextType {
  theme: ThemeVariant;
  setTheme: (theme: ThemeVariant) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const STORAGE_KEY = 'lasertag-theme';

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeVariant>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'terminal' || stored === 'cyber') return stored;
    } catch {}
    return 'cyber';
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, theme);
    document.documentElement.setAttribute('data-theme', theme);
    // Keep browser chrome (iOS address/tool bars) in sync with the page background
    const themeColor = theme === 'terminal' ? '#0C0E0C' : '#0A0C13';
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', themeColor);
  }, [theme]);

  const setTheme = (t: ThemeVariant) => setThemeState(t);
  const toggleTheme = () => setThemeState(prev => prev === 'cyber' ? 'terminal' : 'cyber');

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
